type MetadataKey = string | symbol;
type PropertyKey = string | symbol | undefined;

const metadataStore = new WeakMap<
  object,
  Map<PropertyKey, Map<MetadataKey, unknown>>
>();

function ensureTargetStore(
  target: object,
): Map<PropertyKey, Map<MetadataKey, unknown>> {
  let targetStore = metadataStore.get(target);
  if (!targetStore) {
    targetStore = new Map<PropertyKey, Map<MetadataKey, unknown>>();
    metadataStore.set(target, targetStore);
  }
  return targetStore;
}

function ensurePropertyStore(
  target: object,
  propertyKey?: PropertyKey,
): Map<MetadataKey, unknown> {
  const targetStore = ensureTargetStore(target);
  const key = propertyKey ?? undefined;
  let propertyStore = targetStore.get(key);
  if (!propertyStore) {
    propertyStore = new Map<MetadataKey, unknown>();
    targetStore.set(key, propertyStore);
  }
  return propertyStore;
}

const reflectApi = Reflect as typeof Reflect & {
  decorate?: (
    decorators: Array<(target: object, propertyKey?: PropertyKey) => unknown>,
    target: object,
    propertyKey?: PropertyKey,
    attributes?: PropertyDescriptor,
  ) => unknown;
  defineMetadata?: (
    metadataKey: MetadataKey,
    metadataValue: unknown,
    target: object,
    propertyKey?: PropertyKey,
  ) => void;
  getMetadata?: (
    metadataKey: MetadataKey,
    target: object,
    propertyKey?: PropertyKey,
  ) => unknown;
  hasMetadata?: (
    metadataKey: MetadataKey,
    target: object,
    propertyKey?: PropertyKey,
  ) => boolean;
  metadata?: (
    metadataKey: MetadataKey,
    metadataValue: unknown,
  ) => (target: object, propertyKey?: PropertyKey) => void;
};

if (!reflectApi.defineMetadata) {
  reflectApi.defineMetadata = (
    metadataKey: MetadataKey,
    metadataValue: unknown,
    target: object,
    propertyKey?: PropertyKey,
  ) => {
    ensurePropertyStore(target, propertyKey).set(metadataKey, metadataValue);
  };
}

if (!reflectApi.decorate) {
  reflectApi.decorate = (
    decorators: Array<(target: object, propertyKey?: PropertyKey) => unknown>,
    target: object,
    propertyKey?: PropertyKey,
    attributes?: PropertyDescriptor,
  ) => {
    let result: unknown =
      typeof propertyKey === "undefined" ? target : attributes || target;

    for (let i = decorators.length - 1; i >= 0; i -= 1) {
      const decorator = decorators[i];
      if (typeof propertyKey === "undefined") {
        result = decorator(result as object) || result;
      } else {
        result = decorator(target, propertyKey) || result;
      }
    }

    return result;
  };
}

if (!reflectApi.getMetadata) {
  reflectApi.getMetadata = (
    metadataKey: MetadataKey,
    target: object,
    propertyKey?: PropertyKey,
  ) => {
    const key = propertyKey ?? undefined;
    let current: object | null = target;
    while (current) {
      const targetStore = metadataStore.get(current);
      const propertyStore = targetStore?.get(key);
      if (propertyStore?.has(metadataKey)) {
        return propertyStore.get(metadataKey);
      }
      current = Object.getPrototypeOf(current);
    }
    return undefined;
  };
}

if (!reflectApi.hasMetadata) {
  reflectApi.hasMetadata = (
    metadataKey: MetadataKey,
    target: object,
    propertyKey?: PropertyKey,
  ) => reflectApi.getMetadata!(metadataKey, target, propertyKey) !== undefined;
}

if (!reflectApi.metadata) {
  reflectApi.metadata =
    (metadataKey: MetadataKey, metadataValue: unknown) =>
    (target: object, propertyKey?: PropertyKey) => {
      reflectApi.defineMetadata!(
        metadataKey,
        metadataValue,
        target,
        propertyKey,
      );
    };
}

export {};
